// Structured editors for a provider's wall (how it appears and opens on the Earn page) and its
// hold policy. Like PostbackForm, the JSON text stays the source of truth; these forms rewrite it.
// The small form primitives at the top (Field, Segmented, Disclosure) are shared by the network
// editor (Providers.tsx) and PostbackForm so every section of the drawer reads the same.
import { useEffect, useId, useState, type ReactNode } from 'react';
import {
  ArrowRight,
  Check,
  ChevronRight,
  ClipboardList,
  Gamepad2,
  ImageUp,
  Plus,
  Trash2,
} from 'lucide-react';
import { buildWallUrl, hashedPlaceholders, WallConfigError } from '@gaintplay/core/wall';
import { uploadImage } from '../lib/upload';

interface Wall {
  urlTemplate?: string;
  params?: Record<string, string>;
  display?: {
    title?: string;
    description?: string;
    logoUrl?: string;
    color?: string;
    order?: number;
    badge?: string;
  };
  mode?: 'iframe' | 'tab';
  allow?: ('camera' | 'microphone' | 'fullscreen')[];
  /** Who may open it: the Earn page shows the card locked otherwise, and the server refuses it. */
  gate?: { verifiedEmail?: boolean; minEarnedCoins?: number };
}

/** Sets one condition of the wall's gate; an empty gate is removed. */
function setGate(
  x: Wall,
  key: keyof NonNullable<Wall['gate']>,
  value: boolean | number | undefined,
) {
  const g: NonNullable<Wall['gate']> = { ...(x.gate ?? {}) };
  if (key === 'verifiedEmail') {
    if (value === true) g.verifiedEmail = true;
    else delete g.verifiedEmail;
  } else if (typeof value === 'number' && value > 0) g.minEarnedCoins = Math.floor(value);
  else delete g.minEarnedCoins;
  if (g.verifiedEmail || g.minEarnedCoins) x.gate = g;
  else delete x.gate;
}

const BUILT_IN = ['user_id', 'user_ulid', 'click_id', 'username', 'country', 'email_hash', 'gaid'];
const SAMPLE: Record<string, string> = {
  user_id: 'usr_01J9ABCDEF0123456789XYZ',
  user_ulid: '01J9ABCDEF0123456789XYZ',
  click_id: 'clk_01J9ABCDEF0123456789XYZ',
  username: 'SunnyOtter27',
  country: 'US',
  email_hash: '9f86d0…',
  gaid: '38400000-8cf0-11bd-b23e-10b96e40000d',
};
const BADGES = ['Popular', 'New', 'Hot', 'X2 Bonus'];
const COLORS: [string, string][] = [
  ['#ff6b1a', 'Orange'],
  ['#2563eb', 'Blue'],
  ['#8b5cf6', 'Violet'],
  ['#14b8a6', 'Teal'],
  ['#ef4444', 'Red'],
  ['#f59e0b', 'Amber'],
  ['#10b981', 'Green'],
  ['#111110', 'Ink'],
];
const DEFAULT_TIERS = [
  { minCoins: 2000, days: 7 },
  { minCoins: 4000, days: 15 },
  { minCoins: 15000, days: 30 },
];

/** Placeholders used in a template, outside {md5(...)}/{sha256(...)} wrappers too. */
function placeholders(t: string): string[] {
  const names = new Set<string>();
  for (const m of t.matchAll(/\{([a-z0-9_]+)\}/gi)) names.add(m[1]!);
  return [...names];
}

/* ------------------------------------------------------------------ Form primitives */

/** Small neutral tag after a label, instead of a coloured asterisk. */
export function RequiredTag() {
  return (
    <span className="rounded-full bg-ink/[0.07] px-1.5 py-px text-[11px] font-semibold text-graphite">
      Required
    </span>
  );
}

/**
 * A labelled form row: label above, control, hint below. Pass `id` when the control is a single
 * input with that id; without it the block is a labelled group (several controls).
 */
export function Field({
  label,
  hint,
  required,
  id,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  required?: boolean;
  id?: string;
  children: ReactNode;
}) {
  const labelId = useId();
  const hintId = useId();
  const caption = (
    <span className="flex items-center gap-1.5">
      {label}
      {required && <RequiredTag />}
    </span>
  );
  return (
    <div
      className="min-w-0"
      {...(id ? {} : { role: 'group', 'aria-labelledby': labelId })}
      aria-describedby={hint ? hintId : undefined}
    >
      {id ? (
        <label htmlFor={id} className="mb-1.5 block text-[13px] font-semibold text-graphite">
          {caption}
        </label>
      ) : (
        <div id={labelId} className="mb-1.5 text-[13px] font-semibold text-graphite">
          {caption}
        </div>
      )}
      {children}
      {hint && (
        <p id={hintId} className="mt-1.5 text-[12px] leading-snug text-stone">
          {hint}
        </p>
      )}
    </div>
  );
}

/** Sunken track with the chosen option lifted, same look as the page tabs. */
export function Segmented<K extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: K;
  options: { key: K; label: string; activeClass?: string }[];
  onChange: (k: K) => void;
  label: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex max-w-full flex-wrap gap-0.5 rounded-[12px] bg-ink/[0.06] p-1"
    >
      {options.map((o) => {
        const on = o.key === value;
        return (
          <button
            key={o.key}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.key)}
            className={`h-9 rounded-[9px] px-4 text-[14px] font-semibold transition focus-visible:-outline-offset-2 max-sm:h-10 ${
              on
                ? (o.activeClass ??
                  'bg-paper text-ink shadow-[0_1px_0_#e2ddd2,0_1px_3px_rgba(17,17,16,0.08)]')
                : 'text-graphite hover:bg-paper/50 hover:text-ink'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** "Edit as JSON" style toggle that reveals an advanced block. */
export function Disclosure({
  label,
  hint,
  open,
  onToggle,
  children,
}: {
  label: string;
  hint?: string | undefined;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div className="mt-4">
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className="-ml-2 inline-flex min-h-9 max-w-full items-start gap-1.5 rounded-[8px] px-2 py-1.5 text-left text-[13px] font-semibold text-graphite transition hover:bg-ink/[0.05] hover:text-ink"
      >
        <ChevronRight
          size={15}
          aria-hidden="true"
          className={`mt-px shrink-0 transition ${open ? 'rotate-90' : ''}`}
        />
        <span>
          {label}
          {hint && <span className="font-normal text-stone"> · {hint}</span>}
        </span>
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

/** The same Disclosure keeping its own open state. */
export function Advanced({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Disclosure label={label} hint={hint} open={open} onToggle={() => setOpen((o) => !o)}>
      {children}
    </Disclosure>
  );
}

/** A parse error in one of the JSON blocks: the form above it waits until it's fixed. */
export function JsonError({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="rounded-[12px] border border-berry/30 bg-berry/[0.06] px-4 py-3 text-[14px] text-berry-deep"
    >
      {children}
    </p>
  );
}

const chip = (on: boolean) =>
  `inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-[12px] font-semibold transition max-sm:h-9 ${
    on
      ? 'border-ink bg-ink text-white'
      : 'border-hairline bg-paper text-graphite hover:border-ink hover:text-ink'
  }`;

export function WallForm({
  text,
  onText,
  kind,
}: {
  text: string;
  onText: (t: string) => void;
  kind: string;
}) {
  const [logoBroken, setLogoBroken] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const uid = useId();
  if (!text.trim())
    return (
      <div className="rounded-[16px] border border-dashed border-ink/25 bg-paper px-5 py-7 text-center">
        <p className="mx-auto max-w-sm text-[14px] leading-relaxed text-graphite">
          No wall: this network only sends postbacks and doesn’t appear on the Earn page.
        </p>
        <button
          type="button"
          className="btn-ghost mt-4 h-10 py-0 text-[14px]"
          onClick={() =>
            onText(
              JSON.stringify(
                { urlTemplate: 'https://', params: {}, display: { title: '' }, mode: 'iframe' },
                null,
                2,
              ),
            )
          }
        >
          <Plus size={16} aria-hidden="true" /> Add a wall
        </button>
      </div>
    );
  let w: Wall;
  try {
    w = JSON.parse(text) as Wall;
  } catch {
    return (
      <JsonError>
        The wall JSON doesn’t parse, so this form is paused. Fix it under “Edit as JSON” below.
      </JsonError>
    );
  }
  const d = w.display ?? {};
  const update = (fn: (draft: Wall) => void) => {
    const draft = structuredClone(w);
    draft.display ??= {};
    fn(draft);
    for (const [k, v] of Object.entries(draft.display))
      if (v === '' || v === undefined) delete (draft.display as Record<string, unknown>)[k];
    onText(JSON.stringify(draft, null, 2));
  };
  const setDisplay = (k: keyof NonNullable<Wall['display']>, v: string | number | undefined) =>
    update((x) => {
      (x.display as Record<string, unknown>)[k] = v;
    });

  const onFile = async (file: File) => {
    setUploadErr(null);
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setLogoBroken(false);
      setDisplay('logoUrl', url);
    } catch (e) {
      setUploadErr((e as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const template = w.urlTemplate ?? '';
  const used = placeholders(template);
  const custom = used.filter((p) => !BUILT_IN.includes(p));
  // Values that only go into a hash (CPX's secure hash key): typed hidden, never previewed.
  const secrets = hashedPlaceholders(template).filter((p) => !BUILT_IN.includes(p));
  const accent = d.color || '#ff6b1a';
  const title = d.title || 'Untitled wall';
  const isSurvey = kind === 'survey';
  const mode = w.mode ?? 'iframe';
  const ids = {
    title: `${uid}-title`,
    gateCoins: `${uid}-gate-coins`,
    desc: `${uid}-desc`,
    logo: `${uid}-logo`,
    color: `${uid}-color`,
    badge: `${uid}-badge`,
    order: `${uid}-order`,
    url: `${uid}-url`,
  };

  return (
    <div className="space-y-6">
      {/* ---- Live preview of the Earn card, next to what it shows ---- */}
      <div className="grid gap-5 sm:grid-cols-[240px_minmax(0,1fr)]">
        <div className="max-w-[280px]">
          <div className="mb-1.5 text-[13px] font-semibold text-graphite">
            Preview on the Earn page
          </div>
          <div
            className="overflow-hidden rounded-[24px] border border-hairline bg-paper"
            aria-hidden="true"
          >
            <div
              className="relative flex h-[84px] items-end px-5"
              style={{ background: `color-mix(in oklab, ${accent} 16%, #f6f2ea)` }}
            >
              <span
                className="sticker grid h-14 w-14 translate-y-1/2 place-items-center overflow-hidden rounded-[14px] bg-paper text-[24px] font-extrabold"
                style={{ color: accent }}
              >
                {d.logoUrl && !logoBroken ? (
                  <img
                    src={d.logoUrl}
                    alt=""
                    className="h-full w-full object-contain p-2"
                    onError={() => setLogoBroken(true)}
                  />
                ) : (
                  title[0]
                )}
              </span>
              {d.badge && (
                <span className="sticker absolute right-4 top-4 -rotate-3 rounded-full bg-sun px-2.5 py-1 text-[11px] font-extrabold uppercase">
                  {d.badge}
                </span>
              )}
            </div>
            <div className="px-5 pb-5 pt-10">
              <div className="truncate text-[18px] font-bold tracking-[-0.015em]">{title}</div>
              <div className="mt-1 line-clamp-2 min-h-[20px] text-[13px] text-graphite">
                {d.description}
              </div>
              <div className="mt-4 flex items-center justify-between">
                <span className="chip">
                  {isSurvey ? <ClipboardList size={13} /> : <Gamepad2 size={13} />}
                  {isSurvey ? 'Surveys' : 'Offer wall'}
                </span>
                <span className="grid h-9 w-9 place-items-center rounded-full border-[1.5px] border-ink">
                  <ArrowRight size={15} />
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ---- Appearance ---- */}
        <div className="min-w-0 space-y-4">
          <Field id={ids.title} label="Title" required>
            <input
              id={ids.title}
              className="input h-11 py-0"
              value={d.title ?? ''}
              maxLength={60}
              placeholder="AdBreak Media"
              onChange={(e) => setDisplay('title', e.target.value)}
            />
          </Field>
          <Field
            id={ids.desc}
            label="Description"
            hint="One short line, e.g. “Games and app offers”."
          >
            <input
              id={ids.desc}
              className="input h-11 py-0"
              value={d.description ?? ''}
              maxLength={200}
              onChange={(e) => setDisplay('description', e.target.value)}
            />
          </Field>
          <Field label="Logo">
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                const f = e.dataTransfer.files[0];
                if (f) void onFile(f);
              }}
              className={`flex flex-wrap items-center gap-x-3 gap-y-2 rounded-[12px] border border-dashed p-2.5 transition ${dragging ? 'border-ink bg-coin-tint' : 'border-ink/25 bg-paper'}`}
            >
              <label
                className="btn-ghost h-10 shrink-0 cursor-pointer bg-paper px-3.5 py-0 text-[14px] has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-coin"
                aria-busy={uploading || undefined}
              >
                <ImageUp size={16} aria-hidden="true" /> {uploading ? 'Uploading…' : 'Upload image'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                  className="sr-only"
                  disabled={uploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) void onFile(f);
                  }}
                />
              </label>
              <span className="min-w-0 flex-1 text-[12px] leading-snug text-stone">
                or drop one here. Square works best; it’s resized to 256px.
              </span>
            </div>
            <label htmlFor={ids.logo} className="sr-only">
              Logo link
            </label>
            <input
              id={ids.logo}
              className="input mt-2 h-10 py-0 text-[13px]"
              value={d.logoUrl ?? ''}
              placeholder="…or paste an https:// image link"
              onChange={(e) => {
                setLogoBroken(false);
                setDisplay('logoUrl', e.target.value.trim());
              }}
            />
            <p
              className={`mt-1.5 text-[12px] leading-snug ${uploadErr || (d.logoUrl && logoBroken) ? 'font-semibold text-berry-deep' : 'text-stone'}`}
              role={uploadErr || (d.logoUrl && logoBroken) ? 'alert' : undefined}
            >
              {uploadErr ??
                (d.logoUrl && logoBroken
                  ? 'That image didn’t load. Use a direct https link to a PNG, SVG or WebP.'
                  : 'Leave empty to show the first letter of the title.')}
            </p>
          </Field>
        </div>
      </div>

      <div className="grid gap-x-5 gap-y-5 sm:grid-cols-2">
        <Field id={ids.color} label="Theme color" hint="Tints the card header and the letter logo.">
          <div className="flex items-center gap-2">
            <input
              type="color"
              aria-label="Pick a color"
              className="h-10 w-11 shrink-0 cursor-pointer rounded-[10px] border border-hairline bg-paper p-1"
              value={/^#[0-9a-f]{6}$/i.test(accent) ? accent : '#ff6b1a'}
              onChange={(e) => setDisplay('color', e.target.value)}
            />
            <input
              id={ids.color}
              className="input h-10 py-0 font-mono text-[13px]"
              value={d.color ?? ''}
              placeholder="#ff6b1a"
              maxLength={20}
              onChange={(e) => setDisplay('color', e.target.value.trim())}
            />
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {COLORS.map(([c, name]) => {
              const on = d.color?.toLowerCase() === c;
              return (
                <button
                  key={c}
                  type="button"
                  aria-label={`${name} (${c})`}
                  aria-pressed={on}
                  title={name}
                  onClick={() => setDisplay('color', c)}
                  className={`grid h-7 w-7 place-items-center rounded-full ring-offset-2 max-sm:h-8 max-sm:w-8 ring-offset-canvas transition ${on ? 'ring-2 ring-ink' : 'hover:ring-2 hover:ring-ink/30'}`}
                  style={{ background: c }}
                >
                  {on && (
                    <Check size={13} strokeWidth={3} className="text-white" aria-hidden="true" />
                  )}
                </button>
              );
            })}
          </div>
        </Field>
        <Field id={ids.badge} label="Badge" hint="Small sticker on the card, up to 20 characters.">
          <input
            id={ids.badge}
            className="input h-10 py-0"
            value={d.badge ?? ''}
            maxLength={20}
            placeholder="None"
            onChange={(e) => setDisplay('badge', e.target.value)}
          />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {['', ...BADGES].map((b) => (
              <button
                key={b || 'none'}
                type="button"
                aria-pressed={(d.badge ?? '') === b}
                onClick={() => setDisplay('badge', b)}
                className={chip((d.badge ?? '') === b)}
              >
                {b || 'None'}
              </button>
            ))}
          </div>
        </Field>
        <Field
          id={ids.order}
          label="Position"
          hint="Lower shows first. The first card is the big “Top pick”."
        >
          <input
            id={ids.order}
            className="input h-10 py-0 tabular-nums"
            type="number"
            value={d.order ?? ''}
            placeholder="Automatic"
            onChange={(e) =>
              setDisplay('order', e.target.value === '' ? undefined : Number(e.target.value))
            }
          />
        </Field>
        <Field
          label="Opens"
          hint="Use a new tab if the network refuses to be shown inside other sites."
        >
          <Segmented
            label="Opens"
            value={mode}
            onChange={(m) => update((x) => (x.mode = m))}
            options={[
              { key: 'iframe', label: 'In a popup' },
              { key: 'tab', label: 'In a new tab' },
            ]}
          />
        </Field>
        {mode === 'iframe' && (
          <div className="sm:col-span-2">
            <Field
              label="Let the wall use"
              hint={
                <>
                  Only what the network’s embed code asks for:{' '}
                  <code className="rounded bg-ink/[0.05] px-1">allow="camera …"</code> means Camera.
                  Granted to the wall’s own site only, and the browser still asks the user.
                </>
              }
            >
              <div className="flex flex-wrap gap-1.5">
                {(
                  [
                    ['camera', 'Camera'],
                    ['microphone', 'Microphone'],
                    ['fullscreen', 'Full screen'],
                  ] as const
                ).map(([f, label]) => {
                  const on = (w.allow ?? []).includes(f);
                  return (
                    <button
                      key={f}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        update((x) => {
                          const cur = new Set(x.allow ?? []);
                          if (on) cur.delete(f);
                          else cur.add(f);
                          if (cur.size) x.allow = [...cur];
                          else delete x.allow;
                        })
                      }
                      className={chip(on)}
                    >
                      {on && <Check size={13} aria-hidden="true" />}
                      {label}
                    </button>
                  );
                })}
              </div>
            </Field>
          </div>
        )}
        <div className="sm:col-span-2">
          <Field
            label="Who can open it"
            hint="A network’s own condition for going live (AdGem asks for both). Members see the card locked with the reason until they meet it, and the server refuses to open it for them."
          >
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <label className="flex items-center gap-2 text-[14px]">
                <input
                  type="checkbox"
                  className="h-4 w-4 shrink-0 accent-ink"
                  checked={!!w.gate?.verifiedEmail}
                  onChange={(e) => update((x) => setGate(x, 'verifiedEmail', e.target.checked))}
                />
                Confirmed email address
              </label>
              <label htmlFor={ids.gateCoins} className="flex items-center gap-2 text-[14px]">
                After
                <input
                  id={ids.gateCoins}
                  className="input h-10 w-28 py-0 tabular-nums"
                  type="number"
                  min={0}
                  step={100}
                  value={w.gate?.minEarnedCoins ?? ''}
                  placeholder="0"
                  onChange={(e) =>
                    update((x) =>
                      setGate(
                        x,
                        'minEarnedCoins',
                        e.target.value === '' ? undefined : Number(e.target.value),
                      ),
                    )
                  }
                />
                coins earned from offers (1,000 = US$1)
              </label>
            </div>
          </Field>
        </div>
      </div>

      {/* ---- URL ---- */}
      <div className="space-y-3">
        <Field
          id={ids.url}
          label="Wall URL"
          required
          hint={
            <>
              Paste the network’s wall link and put placeholders where our ids go, anywhere in the
              path or query. <code className="font-mono">{'{gaid}'}</code> is the user’s Google
              advertising ID from the Android app, or{' '}
              <code className="font-mono">00000000-0000-0000-0000-000000000000</code> when we don’t
              have one (website visitors, or the user deleted it), as the old site sent. For a
              signed link, wrap the parts in <code className="font-mono">{'{md5(…)}'}</code> or{' '}
              <code className="font-mono">{'{sha256(…)}'}</code>: CPX Research’s{' '}
              <code className="font-mono">{'secure_hash={md5({user_id}-{secure_hash_key})}'}</code>{' '}
              becomes the md5 of the user’s id, a dash and your key.{' '}
              <code className="font-mono">{'{lower(…)}'}</code> lowercases its text, for a network
              that takes lowercase player ids only (AdGem).
            </>
          }
        >
          <input
            id={ids.url}
            className="input h-11 py-0 font-mono text-[13px]"
            value={template}
            placeholder="https://wall.adbreakmedia.com/{placement_id}/{user_id}"
            spellCheck={false}
            onChange={(e) => update((x) => (x.urlTemplate = e.target.value.trim()))}
          />
        </Field>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-0.5 text-[12px] font-semibold text-stone">Insert</span>
          {BUILT_IN.map((p) => (
            <button
              key={p}
              type="button"
              title={`Add {${p}} to the end of the URL`}
              onClick={() => update((x) => (x.urlTemplate = `${template}{${p}}`))}
              className={`${chip(used.includes(p))} h-7 px-2.5 font-mono font-normal`}
            >
              {`{${p}}`}
            </button>
          ))}
          <button
            type="button"
            title="Add an md5 of the user id and a secret key, like CPX Research's secure_hash"
            onClick={() =>
              update((x) => (x.urlTemplate = `${template}{md5({user_id}-{secure_hash_key})}`))
            }
            className={`${chip(secrets.length > 0)} h-7 px-2.5 font-mono font-normal`}
          >
            {'{md5(…)}'}
          </button>
        </div>
        {custom.length > 0 && (
          <div className="rounded-[14px] border border-hairline bg-paper p-4">
            <div className="text-[13px] font-semibold text-graphite">Your values</div>
            <p className="mt-0.5 text-[12px] text-stone">
              Placeholders that aren’t ours, like a placement id from the network.
            </p>
            <div className="mt-3 space-y-2">
              {custom.map((p) => (
                <div
                  key={p}
                  className="grid items-center gap-1.5 sm:grid-cols-[160px_1fr] sm:gap-3"
                >
                  <label htmlFor={`${uid}-p-${p}`} className="font-mono text-[13px] text-ink">
                    {`{${p}}`}
                    {secrets.includes(p) && (
                      <span className="ml-1.5 font-sans text-[11px] font-semibold text-stone">
                        secret, hashed
                      </span>
                    )}
                  </label>
                  <input
                    id={`${uid}-p-${p}`}
                    className={`input h-10 py-0 font-mono text-[13px] ${w.params?.[p] ? '' : '!border-coin'}`}
                    value={w.params?.[p] ?? ''}
                    placeholder="Required"
                    type={secrets.includes(p) ? 'password' : 'text'}
                    autoComplete={secrets.includes(p) ? 'new-password' : 'off'}
                    spellCheck={false}
                    aria-invalid={!w.params?.[p] || undefined}
                    onChange={(e) =>
                      update((x) => {
                        x.params = { ...(x.params ?? {}), [p]: e.target.value.trim() };
                      })
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        {template && <LinkPreview template={template} params={w.params} />}
      </div>

      <button
        type="button"
        className="btn-ghost h-9 px-3 py-0 text-[13px] text-berry-deep hover:border-berry-deep"
        onClick={() => onText('')}
      >
        <Trash2 size={14} aria-hidden="true" /> Remove wall (postbacks only)
      </button>
    </div>
  );
}

/** The sample user the link preview is built for (SAMPLE above, as a WallContext). */
const SAMPLE_USER = {
  userId: SAMPLE.user_id!,
  clickId: SAMPLE.click_id!,
  username: SAMPLE.username!,
  country: SAMPLE.country!,
  emailHash: '9f86d081884c7d659a2feaa0c55ad015a3bf4f1b2b0b822cd15d6c15b0f00a08',
  gaid: SAMPLE.gaid!,
};

/**
 * The link a sample user would get, built by the same function the API uses
 * (@gaintplay/core/wall), so {md5(...)} and {sha256(...)} show their real hash and secret keys
 * never appear. Problems (a value not filled in, not a link yet) show instead of the link.
 */
function LinkPreview({
  template,
  params,
}: {
  template: string;
  params?: Record<string, string> | undefined;
}) {
  const [state, setState] = useState<{ url?: string; error?: string }>({});
  const paramsKey = JSON.stringify(params ?? {});
  useEffect(() => {
    let live = true;
    buildWallUrl(
      { urlTemplate: template, params: JSON.parse(paramsKey) as Record<string, string> },
      SAMPLE_USER,
    ).then(
      (url) => live && setState({ url }),
      (e: unknown) =>
        live &&
        setState({
          error:
            e instanceof WallConfigError
              ? e.message.replace(
                  /^wall param "(.+)" is not set$/,
                  'Fill in {$1} above to see the link.',
                )
              : 'Not a complete https:// link yet.',
        }),
    );
    return () => {
      live = false;
    };
  }, [template, paramsKey]);
  return (
    <div className="rounded-[12px] border border-hairline bg-paper px-4 py-3 text-[12px]">
      <div className="font-semibold text-graphite">Example link for a user</div>
      {state.error ? (
        <p className="mt-1 font-semibold text-coin-ink">{state.error}</p>
      ) : (
        <code className="mt-1 block break-all leading-relaxed text-ink">{state.url ?? '…'}</code>
      )}
    </div>
  );
}

/** Hold policy: provider default, no hold, or custom tiers. Empty text means the default. */
export function HoldForm({ text, onText }: { text: string; onText: (t: string) => void }) {
  let tiers: { minCoins: number; days: number }[] | null = null;
  try {
    tiers = text.trim() ? (JSON.parse(text) as { tiers: typeof DEFAULT_TIERS }).tiers : null;
  } catch {
    return (
      <JsonError>
        The hold policy JSON doesn’t parse, so this form is paused. Fix it under “Edit as JSON”
        below.
      </JsonError>
    );
  }
  const mode = tiers === null ? 'default' : tiers.length === 0 ? 'none' : 'custom';
  const write = (t: typeof DEFAULT_TIERS | null) =>
    onText(t === null ? '' : JSON.stringify({ tiers: t }, null, 2));
  const fmt = (n: number) => n.toLocaleString('en-US');
  const usd = (coins: number) =>
    (coins / 1000).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  return (
    <div className="space-y-3">
      <Segmented
        label="Hold policy"
        value={mode}
        onChange={(m) =>
          write(m === 'default' ? null : m === 'none' ? [] : tiers?.length ? tiers : DEFAULT_TIERS)
        }
        options={[
          { key: 'default', label: 'Standard' },
          { key: 'custom', label: 'Custom' },
          {
            key: 'none',
            label: 'No hold',
            activeClass: 'bg-sun text-ink shadow-[0_1px_3px_rgba(17,17,16,0.12)]',
          },
        ]}
      />
      <p
        className={`max-w-prose text-[13px] leading-relaxed ${mode === 'none' ? 'rounded-[10px] bg-sun/25 px-3 py-2 font-semibold text-ink' : 'text-graphite'}`}
      >
        {mode === 'default' &&
          'Rewards of 2,000+ coins wait 7 days, 4,000+ wait 15 days and 15,000+ wait 30 days, so the network can still reverse them before they can be cashed out.'}
        {mode === 'none' &&
          'Every reward can be cashed out immediately. Only for networks that never reverse, or that you fully trust.'}
        {mode === 'custom' && 'Bigger rewards wait longer. The highest matching tier applies.'}
      </p>
      {mode === 'custom' && tiers && (
        <div className="divide-y divide-hairline rounded-[14px] border border-hairline bg-paper">
          {tiers.map((t, i) => (
            <div
              key={i}
              className="flex flex-wrap items-center gap-x-2 gap-y-2 px-3 py-2.5 text-[14px] text-graphite sm:px-4"
            >
              <span>Rewards of</span>
              <input
                className="input h-9 w-28 px-2.5 py-0 text-right text-[14px] font-semibold tabular-nums"
                type="number"
                min={0}
                aria-label={`Tier ${i + 1}: smallest reward in coins`}
                value={t.minCoins}
                onChange={(e) =>
                  write(
                    tiers!.map((x, j) =>
                      j === i ? { ...x, minCoins: Number(e.target.value) } : x,
                    ),
                  )
                }
              />
              <span>coins or more wait</span>
              <input
                className="input h-9 w-20 px-2.5 py-0 text-right text-[14px] font-semibold tabular-nums"
                type="number"
                min={0}
                max={120}
                aria-label={`Tier ${i + 1}: days to wait`}
                value={t.days}
                onChange={(e) =>
                  write(
                    tiers!.map((x, j) => (j === i ? { ...x, days: Number(e.target.value) } : x)),
                  )
                }
              />
              <span>days</span>
              <span className="text-[12px] tabular-nums text-stone">≈ {usd(t.minCoins)}</span>
              <button
                type="button"
                className="ml-auto grid h-9 w-9 place-items-center rounded-[8px] text-stone transition hover:bg-berry/10 hover:text-berry-deep"
                aria-label={`Remove tier ${i + 1}`}
                onClick={() => write(tiers!.filter((_, j) => j !== i))}
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
          {tiers.length < 10 && (
            <button
              type="button"
              className="flex h-11 w-full items-center gap-2 rounded-b-[14px] px-4 text-[13px] font-semibold text-graphite transition hover:bg-[#fbf8f3] hover:text-ink"
              onClick={() =>
                write([
                  ...tiers!,
                  { minCoins: (tiers!.at(-1)?.minCoins ?? 0) * 2 || 1000, days: 7 },
                ])
              }
            >
              <Plus size={14} aria-hidden="true" /> Add a tier
            </button>
          )}
        </div>
      )}
    </div>
  );
}
